import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search } from 'lucide-react';
import { DataTable } from '../../../../../components/ui/DataTable';
import { fetchZoneCommunes } from '../../../../../features/zones/zonesThunks';
import {
  setCommunesSearch,
  setCommunesPage,
  setCommunesRowsPerPage,
} from '../../../../../features/zones/zonesSlice';
import { ZONE_COUNTRIES } from '../../../../../features/zones/zonesConstants';

/**
 * Column set for manually built coverage (Chile).
 *
 * @type {Array<object>}
 */
const CL_COLUMNS = [
  {
    header: 'COMUNA',
    cell: (row) => (
      <span className="font-semibold text-ninja-dark dark:text-white">{row.name}</span>
    ),
  },
  { header: 'REGIÓN', cell: (row) => row.region_name || '-' },
  {
    header: 'UBIGEO CODE',
    cell: (row) => (
      <span className="font-mono text-xs text-text-secondary">{row.ubigeo_code || '-'}</span>
    ),
  },
];

/**
 * Column set for spreadsheet-loaded coverage (Mexico), which is keyed by
 * postal code and settlement rather than by commune alone.
 *
 * @type {Array<object>}
 */
const MX_COLUMNS = [
  {
    header: 'COMUNA',
    cell: (row) => (
      <span className="font-semibold text-ninja-dark dark:text-white">{row.name}</span>
    ),
  },
  {
    header: 'CÓDIGO POSTAL',
    cell: (row) => (
      <span className="font-mono text-xs text-text-secondary">{row.postal_code || '-'}</span>
    ),
  },
  { header: 'ASENTAMIENTO', cell: (row) => row.settlement || '-' },
  { header: 'REGIÓN', cell: (row) => row.region_name || '-' },
];

/**
 * Coverage tab: the communes a zone reaches, paginated server-side.
 *
 * @param {object} props
 * @param {number|string} props.zoneId
 * @param {string} props.countryCode - ISO2 code selecting the column set
 */
export const ZoneCommunesTab = ({ zoneId, countryCode }) => {
  const dispatch = useDispatch();
  const { communes, communesCount, loadingCommunes, communesFilters } = useSelector(
    (state) => state.zones
  );

  const [searchInput, setSearchInput] = useState(communesFilters.search);

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== communesFilters.search) {
        dispatch(setCommunesSearch(searchInput));
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, communesFilters.search, dispatch]);

  // Fetch when relevant filters change
  useEffect(() => {
    dispatch(fetchZoneCommunes(zoneId));
  }, [
    dispatch,
    zoneId,
    communesFilters.search,
    communesFilters.page,
    communesFilters.rowsPerPage,
  ]);

  const isMassive = countryCode === ZONE_COUNTRIES.MX;
  const columns = isMassive ? MX_COLUMNS : CL_COLUMNS;

  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border overflow-hidden animate-in slide-in-from-bottom-2 duration-300">
      <div className="p-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative w-full sm:max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={18} className="text-text-muted" />
          </div>
          <input
            type="text"
            placeholder={
              isMassive
                ? 'Buscar por comuna, código postal...'
                : 'Buscar por comuna, región...'
            }
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-ninja-green focus:border-transparent transition-all dark:text-white"
          />
        </div>
        <span className="text-sm text-text-secondary whitespace-nowrap">
          {communesCount.toLocaleString('es-CL')}{' '}
          {communesFilters.search ? 'resultados' : 'comunas cubiertas'}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={communes}
        totalItems={communesCount}
        currentPage={communesFilters.page}
        itemsPerPage={communesFilters.rowsPerPage}
        isLoading={loadingCommunes}
        itemLabel="comunas"
        onPageChange={(page) => dispatch(setCommunesPage(page))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setCommunesRowsPerPage(rowsPerPage))}
      />
    </div>
  );
};
